'use client';

import { forwardRef, useEffect, useState } from 'react';
import type {
  ChangeEvent,
  ComponentPropsWithoutRef,
  FC,
  KeyboardEvent,
  ReactNode,
  RefObject,
} from 'react';

import type { VirtualItem } from '@tanstack/react-virtual';
import { DateTime } from 'luxon';

import joinChat from '@wwe-portal/ui/assets/images/chat/join-chat.svg';
import { Button } from '@wwe-portal/ui/components/button';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { Skeleton } from '@wwe-portal/ui/components/skeleton';
import {
  Tooltip,
  TooltipPortal,
  TooltipTrigger,
  TooltipContent,
  TooltipArrow,
} from '@wwe-portal/ui/components/tooltip';
import { Each } from '@wwe-portal/ui/components/utils';
import { cn } from '@wwe-portal/ui/lib/lib';

interface ChatUnavailableBodyProps {
  title: ReactNode;
  description: ReactNode;
  loginButtons?: ReactNode;
}
const ChatNotAvailableBody: FC<ChatUnavailableBodyProps> = ({
  title,
  description,
  loginButtons,
}) => {
  return (
    <div className="flex max-w-[370px] flex-col items-center justify-center">
      <img alt="join chat" src={joinChat as never} />
      <div className="text-text500 mb-3 text-center text-base">{title}</div>
      <div className="text-text600 mb-4 text-center text-xs">{description}</div>
      {loginButtons}
    </div>
  );
};

interface ChatInputProps {
  disabled: boolean;
  buttonDisabled: boolean;
  onSubmit: () => void;
  onInputChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  tooltipContent?: ReactNode;
  onTooltipClose?: () => void;
}
export const ChatInput: FC<ChatInputProps> = ({
  disabled,
  buttonDisabled,
  onSubmit,
  onInputChange,
  inputRef,
  tooltipContent,
  onTooltipClose,
}) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <Tooltip
      onOpenChange={(value) => {
        if (!value) onTooltipClose?.();
      }}
      open={Boolean(tooltipContent)}
    >
      <TooltipTrigger asChild>
        <div className="relative text-[0px]">
          <textarea
            className="bg-back300 border-back300 text-text500 placeholder:text-text700 focus:border-borderColor2 box-border h-12 w-full resize-none rounded-md border border-solid py-4 pl-5 pr-[120px] text-xs focus:outline-none"
            disabled={disabled}
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message"
            ref={inputRef}
          />

          <Button
            className={cn(
              'absolute right-1 top-1/2 h-10 -translate-y-1/2 leading-5 disabled:bg-[#aee9ff] disabled:text-white'
            )}
            disabled={buttonDisabled}
            onClick={onSubmit}
            variant="blue"
          >
            <div className="flex items-center gap-2">
              <span className="hidden text-xs md:block">Send</span>
              <IcoMoonIcon className="size-5 text-xl" iconName="send-message" />
            </div>
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent align="center">
          {tooltipContent}
          <TooltipArrow />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
};

const MessageItemLayout = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'> & {
    imageSlot: ReactNode;
    nicknameSlot: ReactNode;
    messageSlot: ReactNode;
  }
>(({ imageSlot, nicknameSlot, messageSlot, ...props }, ref) => {
  return (
    <div {...props} className="flex min-h-12 gap-4 pb-8" ref={ref}>
      {imageSlot}
      <div className="flex flex-1 flex-col gap-2">
        <div>{nicknameSlot}</div>
        <div className="text-text600 text-xs">{messageSlot}</div>
      </div>
    </div>
  );
});
MessageItemLayout.displayName = 'MessageItemLayout';

interface MessageListProps {
  messages: {
    ts: number;
    userId: string;
    message: string;
    avatar: string;
  }[];
  items: VirtualItem[];
  totalSize: number;
  measureElement: (node: Element | null) => void;
  UserLink: FC<{ userId: string; children: ReactNode }>;
  isLoading?: boolean;
}
const TIME_INTERVAL = 10_000;
const MILISECONDS_PER_MINUTE = 60_000;
const RelativeTime: FC<{ ts: number; now: number }> = ({ ts, now }) => {
  if (now - ts < MILISECONDS_PER_MINUTE) return <>few seconds ago</>;
  return (
    <>
      {DateTime.fromMillis(ts).toRelative({
        base: DateTime.fromMillis(now),
        round: true,
      })}
    </>
  );
};

const MessageList = forwardRef<HTMLDivElement, MessageListProps>(
  (
    { messages, items, measureElement, totalSize, UserLink, isLoading },
    ref
  ) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
      const timer = setInterval(() => setNow(Date.now()), TIME_INTERVAL);
      return () => {
        clearInterval(timer);
      };
    }, []);

    useEffect(() => {
      if (typeof ref !== 'object' || !ref?.current) return;
      const el = ref.current;
      const handleScroll = (e: WheelEvent) => {
        e.preventDefault();
        const currentTarget = e.currentTarget;

        if (currentTarget) {
          (currentTarget as HTMLElement).scrollTop -= e.deltaY;
        }
      };

      el.addEventListener('wheel', handleScroll, {
        passive: false,
      });
      return () => {
        el.removeEventListener('wheel', handleScroll);
      };
    }, [ref]);

    return (
      <div
        className="flex h-full flex-col gap-8 overflow-y-auto"
        data-id="chat"
        ref={ref}
        style={{
          transform: 'scaleY(-1)',
        }}
      >
        <div
          className="relative min-h-[400px] w-full"
          style={{
            height: totalSize,
          }}
        >
          <Each
            data={items}
            render={(virtualItem) => {
              const item = messages[virtualItem.index];
              if (isLoading || item == null)
                return (
                  <MessageItemLayout
                    data-index={virtualItem.index}
                    imageSlot={<Skeleton className="size-8 rounded-full" />}
                    key={virtualItem.key}
                    messageSlot={<Skeleton className="h-4 w-4/5" />}
                    nicknameSlot={<Skeleton className="h-4 w-20" />}
                    ref={measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualItem.start}px) scaleY(-1)`,
                    }}
                  />
                );

              return (
                <MessageItemLayout
                  data-index={virtualItem.index}
                  imageSlot={
                    <img
                      alt={item.userId}
                      className="h-8 w-8 rounded-full"
                      height={32}
                      src={item.avatar}
                      width={32}
                    />
                  }
                  key={virtualItem.key}
                  messageSlot={item.message}
                  nicknameSlot={
                    <>
                      <UserLink userId={item.userId}>
                        <span className="text-text300 mr-2 text-xs">
                          {item.userId}
                        </span>
                      </UserLink>

                      <span className="text-text600 text-2xs">
                        <RelativeTime now={now} ts={item.ts - TIME_INTERVAL} />
                      </span>
                    </>
                  }
                  ref={measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px) scaleY(-1)`,
                  }}
                />
              );
            }}
          />
        </div>
      </div>
    );
  }
);
MessageList.displayName = 'MessageList';

export interface ChatPannelProps {
  header: string;
  headerTooltip: ReactNode;
  FactionLink: FC<{ factionName: string; children: ReactNode }>;
  factionName?: string;
  unavailableProps?: ChatUnavailableBodyProps;
  inputProps?: ChatInputProps;
  messageProps?: MessageListProps;
  messageListRef?: RefObject<HTMLDivElement | null>;
  className?: string;
}

export const ChatPannel: FC<ChatPannelProps> = ({
  header,
  headerTooltip,
  FactionLink,
  factionName,
  unavailableProps,
  inputProps,
  messageProps,
  messageListRef,
  className,
}) => {
  return (
    <div
      className={cn('bg-back400 font-poppins flex h-full flex-col', className)}
    >
      <div className="grow-0 border-0 border-b border-solid border-b-[#31365e] px-4 py-2">
        <div className="flex items-center justify-between">
          <span className="text-text500 capitalize">{header}</span>
          {headerTooltip}
        </div>

        {factionName && (
          <FactionLink factionName={factionName}>
            <div className="text-text300 text-xl capitalize">{factionName}</div>
          </FactionLink>
        )}
      </div>
      {unavailableProps && (
        <div className="flex min-h-[400px] flex-1 flex-col items-center justify-center p-8">
          <ChatNotAvailableBody {...unavailableProps} />
        </div>
      )}
      {messageProps && (
        <div className="flex min-h-[400px] flex-1 flex-col px-8 pt-8">
          <MessageList {...messageProps} ref={messageListRef} />
        </div>
      )}
      {inputProps && (
        <div className="grow-0 p-8">
          <ChatInput {...inputProps} />
        </div>
      )}
    </div>
  );
};
